import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/** A clinic staff member linked to a patient (Tilknytninger). */
export interface PatientStaffAssignment {
  id: string;
  patient_id: string;
  user_id: string;
  role: string | null;
  created_at: string;
  name: string;
  email: string;
}

export const patientStaffAssignmentsQueryKey = (patientId: string | null | undefined) =>
  ['patient-staff-assignments', patientId ?? null] as const;

export function usePatientStaffAssignments(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientStaffAssignmentsQueryKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientStaffAssignment[]> => {
      if (!patientId) return [];
      const { data, error } = await supabase
        .from('patient_staff_assignments')
        .select(
          'id, patient_id, user_id, role, created_at, user:users!patient_staff_assignments_user_id_fkey(name, email)',
        )
        .eq('patient_id', patientId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? [])
        .map((row) => {
          const user = row.user as { name: string | null; email: string } | null;
          return {
            id: row.id,
            patient_id: row.patient_id,
            user_id: row.user_id,
            role: row.role,
            created_at: row.created_at,
            name: user?.name ?? user?.email ?? '',
            email: user?.email ?? '',
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'da'));
    },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, patientId: string) {
  void qc.invalidateQueries({ queryKey: patientStaffAssignmentsQueryKey(patientId) });
  // "Kun mine patienter" on the patient list depends on these links.
  void qc.invalidateQueries({ queryKey: ['patients', 'list'] });
}

export function useAddPatientStaffAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { patientId: string; userId: string }) => {
      // clinic_id and created_by_user_id are set server-side by the insert trigger.
      const { error } = await supabase
        .from('patient_staff_assignments')
        .upsert(
          { patient_id: input.patientId, user_id: input.userId },
          { onConflict: 'patient_id,user_id', ignoreDuplicates: true },
        );
      if (error) throw error;
    },
    onSuccess: (_data, vars) => invalidate(qc, vars.patientId),
  });
}

export function useRemovePatientStaffAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { patientId: string; userId: string }) => {
      const { error } = await supabase
        .from('patient_staff_assignments')
        .delete()
        .eq('patient_id', input.patientId)
        .eq('user_id', input.userId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => invalidate(qc, vars.patientId),
  });
}
